<!-- Table Container with Loading Overlay -->
<div data-testid="data-table-container" class="relative overflow-x-auto" #tableContainer>
  <p-table
    data-testid="data-table"
    [value]="dataSource"
    [tableStyle]="tableStyle"
    showGridlines
    [scrollable]="true"
    [scrollHeight]="scrollHeight"
    styleClass="w-full"
    [class.checkbox-fixed]="hasCheckColumn"
  >
    <!-- Column Group for Width Control -->
    <ng-template pTemplate="colgroup">
      @if (hasCheckColumn) {
        <col style="width: 50px; min-width: 50px; max-width: 50px" />
      }
      @if (hasSerialColumn) {
        <col
          [style.width.px]="getSerialColumnWidth()"
          [style.min-width.px]="getSerialColumnWidth()"
          [style.max-width.px]="getSerialColumnWidth()"
        />
      }
      @for (col of columns; track $index) {
        <col [style.width.px]="getColumnWidth(col)" />
      }
    </ng-template>

    <!-- Table header -->
    <ng-template pTemplate="header">
      <tr data-testid="data-table-header-row">
        @if (hasCheckColumn && hasHeaderCheckbox) {
          <th
            data-testid="table-header-checkbox"
            scope="col"
            class="bg-blue-50 font-semibold text-center text-sm checked-cell"
          >
            <p-checkbox
              data-testid="table-header-checkbox-input"
              size="small"
              [binary]="true"
              (onChange)="onSelectionAll()"
              [ngModel]="isAllSelected"
              [ngClass]="{ 'has-disabled-rows': hasDisabledRows() }"
            ></p-checkbox>
          </th>
        }
        @if (hasCheckColumn && !hasHeaderCheckbox) {
          <th
            data-testid="table-header-checkbox-placeholder"
            scope="col"
            class="bg-blue-50 font-semibold text-center text-sm checked-cell"
          ></th>
        }
        @if (hasSerialColumn) {
          <th
            data-testid="table-header-serial"
            scope="col"
            class="bg-blue-50 font-semibold p-2 text-center text-sm header-cell-number"
            [style.width.px]="getSerialColumnWidth()"
            [style.min-width.px]="getSerialColumnWidth()"
            [style.max-width.px]="getSerialColumnWidth()"
          >
            {{ "common.table.serial_number" | translate }}
          </th>
        }
        @for (col of columns; track $index) {
          <th
            [attr.data-testid]="'table-header-' + col.prop"
            scope="col"
            class="bg-blue-50 font-semibold p-2 text-sm header-cell"
            [ngClass]="{
              'text-left': col.align === 'left',
              'text-center': col.align === 'center' || !col.align,
              'text-right': col.align === 'right',
              'cursor-pointer': col.sortable,
              'hover:bg-blue-100': col.sortable,
            }"
            [ngStyle]="{ 'width.px': getColumnWidth(col) }"
            (click)="onSort(col)"
            (keydown.enter)="onSort(col)"
          >
            <div
              class="flex items-center gap-2"
              [ngClass]="{
                'justify-start': col.align === 'left',
                'justify-center': col.align === 'center' || !col.align,
                'justify-end': col.align === 'right',
              }"
              [pTooltip]="
                col.headerTooltip ? (col.headerTooltip | translate) : null
              "
              tooltipPosition="top"
              tooltipStyleClass="custom-tooltip"
              [showDelay]="300"
              [hideDelay]="0"
            >
              <span>{{ col.label | translate }}</span>
              @if (col.required) {
                <span class="text-red-500">*</span>
              }
              @if (col.sortable) {
                @if (sortField === col.prop) {
                  @if (sortOrder === 1) {
                    <!-- Sort Increase -->
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      data-p-icon="sort-amount-up-alt"
                      class="p-datatable-sort-icon p-icon"
                    >
                      <g clip-path="url(#pui_id_111)">
                        <path
                          d="M3.63435 0.19871C3.57113 0.135484 3.49887 0.0903226 3.41758 0.0541935C3.255 -0.0180645 3.06532 -0.0180645 2.90274 0.0541935C2.82145 0.0903226 2.74919 0.135484 2.68597 0.19871L0.427901 2.45677C0.165965 2.71871 0.165965 3.15226 0.427901 3.41419C0.689836 3.67613 1.12338 3.67613 1.38532 3.41419L2.48726 2.31226V13.3226C2.48726 13.6929 2.79435 14 3.16467 14C3.535 14 3.84209 13.6929 3.84209 13.3226V2.31226L4.94403 3.41419C5.07951 3.54968 5.25113 3.6129 5.42274 3.6129C5.59435 3.6129 5.76597 3.54968 5.90145 3.41419C6.16338 3.15226 6.16338 2.71871 5.90145 2.45677L3.64338 0.19871H3.63435ZM13.7685 13.3226C13.7685 12.9523 13.4615 12.6452 13.0911 12.6452H7.22016C6.84984 12.6452 6.54274 12.9523 6.54274 13.3226C6.54274 13.6929 6.84984 14 7.22016 14H13.0911C13.4615 14 13.7685 13.6929 13.7685 13.3226ZM7.22016 8.58064C6.84984 8.58064 6.54274 8.27355 6.54274 7.90323C6.54274 7.5329 6.84984 7.22581 7.22016 7.22581H9.47823C9.84855 7.22581 10.1556 7.5329 10.1556 7.90323C10.1556 8.27355 9.84855 8.58064 9.47823 8.58064H7.22016ZM7.22016 5.87097H7.67177C8.0421 5.87097 8.34919 5.56387 8.34919 5.19355C8.34919 4.82323 8.0421 4.51613 7.67177 4.51613H7.22016C6.84984 4.51613 6.54274 4.82323 6.54274 5.19355C6.54274 5.56387 6.84984 5.87097 7.22016 5.87097ZM11.2847 11.2903H7.22016C6.84984 11.2903 6.54274 10.9832 6.54274 10.6129C6.54274 10.2426 6.84984 9.93548 7.22016 9.93548H11.2847C11.655 9.93548 11.9621 10.2426 11.9621 10.6129C11.9621 10.9832 11.655 11.2903 11.2847 11.2903Z"
                          fill="currentColor"
                        />
                      </g>
                      <defs>
                        <clipPath id="pui_id_111">
                          <rect width="14" height="14" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  } @else {
                    <!-- Sort Decrease -->
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      data-p-icon="sort-amount-down"
                      class="p-datatable-sort-icon p-icon"
                    >
                      <g clip-path="url(#pui_id_112)">
                        <path
                          d="M4.93953 10.5858L3.83759 11.6877V0.677419C3.83759 0.307097 3.53049 0 3.16017 0C2.78985 0 2.48275 0.307097 2.48275 0.677419V11.6877L1.38082 10.5858C1.11888 10.3239 0.685331 10.3239 0.423396 10.5858C0.16146 10.8477 0.16146 11.2813 0.423396 11.5432L2.68146 13.8013C2.74469 13.8645 2.81694 13.9097 2.89823 13.9458C2.97952 13.9819 3.06985 14 3.16017 14C3.25049 14 3.33178 13.9819 3.42211 13.9458C3.5034 13.9097 3.57565 13.8645 3.63888 13.8013L5.89694 11.5432C6.15888 11.2813 6.15888 10.8477 5.89694 10.5858C5.63501 10.3239 5.20146 10.3239 4.93953 10.5858ZM13.0957 0H7.22468C6.85436 0 6.54726 0.307097 6.54726 0.677419C6.54726 1.04774 6.85436 1.35484 7.22468 1.35484H13.0957C13.466 1.35484 13.7731 1.04774 13.7731 0.677419C13.7731 0.307097 13.466 0 13.0957 0ZM7.22468 5.41935H9.48275C9.85307 5.41935 10.1602 5.72645 10.1602 6.09677C10.1602 6.4671 9.85307 6.77419 9.48275 6.77419H7.22468C6.85436 6.77419 6.54726 6.4671 6.54726 6.09677C6.54726 5.72645 6.85436 5.41935 7.22468 5.41935ZM7.6763 8.12903H7.22468C6.85436 8.12903 6.54726 8.43613 6.54726 8.80645C6.54726 9.17677 6.85436 9.48387 7.22468 9.48387H7.6763C8.04662 9.48387 8.35372 9.17677 8.35372 8.80645C8.35372 8.43613 8.04662 8.12903 7.6763 8.12903ZM7.22468 2.70968H11.2892C11.6595 2.70968 11.9666 3.01677 11.9666 3.3871C11.9666 3.75742 11.6595 4.06452 11.2892 4.06452H7.22468C6.85436 4.06452 6.54726 3.75742 6.54726 3.3871C6.54726 3.01677 6.85436 2.70968 7.22468 2.70968Z"
                          fill="currentColor"
                        />
                      </g>
                      <defs>
                        <clipPath id="pui_id_112">
                          <rect width="14" height="14" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  }
                } @else {
                  <!-- Unsorting -->
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    data-p-icon="sort-alt"
                    class="p-datatable-sort-icon p-icon"
                  >
                    <g clip-path="url(#pui_id_12)">
                      <path
                        d="M5.64515 3.61291C5.47353 3.61291 5.30192 3.54968 5.16644 3.4142L3.38708 1.63484L1.60773 3.4142C1.34579 3.67613 0.912244 3.67613 0.650309 3.4142C0.388374 3.15226 0.388374 2.71871 0.650309 2.45678L2.90837 0.198712C3.17031 -0.0632236 3.60386 -0.0632236 3.86579 0.198712L6.12386 2.45678C6.38579 2.71871 6.38579 3.15226 6.12386 3.4142C5.98837 3.54968 5.81676 3.61291 5.64515 3.61291Z"
                        fill="currentColor"
                      />
                      <path
                        d="M3.38714 14C3.01681 14 2.70972 13.6929 2.70972 13.3226V0.677419C2.70972 0.307097 3.01681 0 3.38714 0C3.75746 0 4.06456 0.307097 4.06456 0.677419V13.3226C4.06456 13.6929 3.75746 14 3.38714 14Z"
                        fill="currentColor"
                      />
                      <path
                        d="M10.6129 14C10.4413 14 10.2697 13.9368 10.1342 13.8013L7.87611 11.5432C7.61418 11.2813 7.61418 10.8477 7.87611 10.5858C8.13805 10.3239 8.5716 10.3239 8.83353 10.5858L10.6129 12.3652L12.3922 10.5858C12.6542 10.3239 13.0877 10.3239 13.3497 10.5858C13.6116 10.8477 13.6116 11.2813 13.3497 11.5432L11.0916 13.8013C10.9561 13.9368 10.7845 14 10.6129 14Z"
                        fill="currentColor"
                      />
                      <path
                        d="M10.6129 14C10.2426 14 9.93552 13.6929 9.93552 13.3226V0.677419C9.93552 0.307097 10.2426 0 10.6129 0C10.9833 0 11.2904 0.307097 11.2904 0.677419V13.3226C11.2904 13.6929 10.9832 14 10.6129 14Z"
                        fill="currentColor"
                      />
                    </g>
                    <defs>
                      <clipPath id="pui_id_12">
                        <rect width="14" height="14" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                }
              }
            </div>
          </th>
        }
      </tr>
    </ng-template>

    <!-- Table body -->
    <ng-template pTemplate="body" let-rowData let-rowIndex="rowIndex">
      <tr [attr.data-testid]="'table-row-' + rowIndex">
        @if (hasCheckColumn) {
          <td
            [attr.data-testid]="'table-cell-checkbox-' + rowIndex"
            class="p-2 text-center text-xs checked-cell"
            [ngClass]="{ 'disabled-cell': isRowDisabled(rowData) }"
          >
            <p-checkbox
              [attr.data-testid]="'table-cell-checkbox-input-' + rowIndex"
              size="small"
              [binary]="true"
              [ngModel]="isRowSelected(rowData)"
              [disabled]="isRowDisabled(rowData)"
              (onChange)="onSelectionChange($event, rowData)"
              [ngClass]="{ 'p-checkbox-disabled': isRowDisabled(rowData) }"
            ></p-checkbox>
          </td>
        }
        @if (hasSerialColumn) {
          <td
            [attr.data-testid]="'table-cell-serial-' + rowIndex"
            class="p-2 text-center text-gray-700 text-xs"
            [style.width.px]="getSerialColumnWidth()"
            [style.min-width.px]="getSerialColumnWidth()"
            [style.max-width.px]="getSerialColumnWidth()"
          >
            <div class="serial-cell-content">
              @if (hasExpandableChildren(rowData)) {
                <button
                  type="button"
                  class="package-expand-button"
                  [attr.aria-label]="isRowExpanded(rowData) ? 'Hide child SKUs' : 'Show child SKUs'"
                  (click)="toggleRowExpansion($event, rowData)"
                >
                  <i [class]="isRowExpanded(rowData) ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"></i>
                </button>
              }
              <span>{{ rowIndex + 1 + first }}</span>
            </div>
          </td>
        }
        @for (col of columns; track $index) {
          <td
            [attr.data-testid]="'table-cell-' + col.prop + '-' + rowIndex"
            class="p-2 truncate-cell text-gray-700 text-xs"
            [ngClass]="{
              'status-cell': col.type === 'status',
              'actions-cell': col.type === 'actions',
              'asset-cell': col.template === 'assetTemplate',
              'text-left': col.align === 'left',
              'text-center': col.align === 'center',
              'text-right': col.align === 'right',
            }"
            [class]="col.styleClass || ''"
            [ngStyle]="{
              'max-width.px': getColumnWidth(col),
              'min-width.px': getColumnWidth(col),
            }"
            [pTooltip]="
              shouldShowTooltip(rowData[col.prop], col)
                ? rowData[col.prop]
                : null
            "
            tooltipPosition="top"
            tooltipStyleClass="custom-tooltip"
            [showDelay]="300"
            [hideDelay]="0"
          >
            @switch (col.type) {
              @case ("status") {
                <ng-container
                  [ngTemplateOutlet]="statusTemplate || defaultStatusTemplate"
                  [ngTemplateOutletContext]="{
                    $implicit: rowData[col.prop],
                    rowData: rowData,
                  }"
                ></ng-container>
              }
              @case ("actions") {
                <ng-container
                  [ngTemplateOutlet]="
                    actionsTemplate ||
                    contentActionsTemplate ||
                    defaultActionsTemplate
                  "
                  [ngTemplateOutletContext]="{ $implicit: rowData }"
                ></ng-container>
              }
              @case ("custom") {
                <ng-container
                  [ngTemplateOutlet]="customTemplate"
                  [ngTemplateOutletContext]="{
                    $implicit: rowData[col.prop],
                    rowData: rowData,
                    column: col,
                  }"
                ></ng-container>
              }
              @case ("select") {
                <div class="flex flex-col w-full">
                  <p-select
                    [attr.data-testid]="'table-select-' + col.prop + '-' + rowIndex"
                    [options]="getTranslatedOptions(col.options || [])"
                    [ngModel]="rowData[col.prop]"
                    (ngModelChange)="
                      onCellValueChange(rowData, col.prop, $event)
                    "
                    optionLabel="label"
                    optionValue="value"
                    [required]="col.required || false"
                    [disabled]="
                      getColumnDisabled(col, rowData) || isRowDisabled(rowData)
                    "
                    [placeholder]="
                      col.placeholder ? (col.placeholder | translate) : ''
                    "
                    [styleClass]="
                      getFieldError
                        ? getFieldError(rowIndex, col.prop)
                          ? 'w-full p-invalid'
                          : 'w-full'
                        : 'w-full'
                    "
                    [style]="{
                      'max-width': getColumnWidth(col)
                        ? getColumnWidth(col) + 'px'
                        : '100%',
                      width: '100%',
                    }"
                    [showClear]="false"
                    [filter]="col.filter ?? false"
                    [filterBy]="'label'"
                    appendTo="body"
                  ></p-select>
                  @if (getFieldError && getFieldError(rowIndex, col.prop)) {
                    <small class="text-red-500 text-xs mt-1 message-error">
                      {{ getFieldError(rowIndex, col.prop) }}
                    </small>
                  }
                </div>
              }
              @case ("inputNumber") {
                <div
                  class="flex flex-col w-full relative"
                  (click)="
                    onInputCellClick($event, rowIndex, col.prop, 'inputNumber');
                    $event.stopPropagation()
                  "
                  (keydown.enter)="
                    onInputCellClick($event, rowIndex, col.prop, 'inputNumber')
                  "
                  (mousedown)="$event.stopPropagation()"
                >
                  <p-inputNumber
                    [attr.data-testid]="'table-input-number-' + col.prop + '-' + rowIndex"
                    [(ngModel)]="rowData[col.prop]"
                    (ngModelChange)="
                      onInputNumberChange($event, rowData, col.prop)
                    "
                    (onBlur)="onInputNumberBlur($event, rowData, col.prop)"
                    (onMousedown)="$event.stopPropagation()"
                    [required]="col.required || false"
                    [min]="col.min || 0"
                    [max]="
                      col.max !== undefined
                        ? getMaxValue(col, rowData)
                        : 999999999
                    "
                    [step]="col.step || 1"
                    [placeholder]="
                      col.placeholder ? (col.placeholder | translate) : ''
                    "
                    [styleClass]="
                      getFieldError
                        ? getFieldError(rowIndex, col.prop)
                          ? 'w-full p-invalid'
                          : 'w-full'
                        : 'w-full'
                    "
                    [style]="{
                      'max-width': getColumnWidth(col)
                        ? getColumnWidth(col) + 'px'
                        : '100%',
                      width: '100%',
                    }"
                    [showButtons]="col.showButtons || false"
                    [useGrouping]="col.useGrouping !== false"
                    [inputId]="'input-' + rowIndex + '-' + col.prop"
                  ></p-inputNumber>
                  @if (getFieldError && getFieldError(rowIndex, col.prop)) {
                    <span
                      class="validation-icon absolute right-2 top-1/2 -translate-y-1/2 text-red-500 cursor-pointer"
                      [pTooltip]="getFieldError(rowIndex, col.prop) | translate"
                      tooltipPosition="top"
                      tooltipStyleClass="custom-tooltip"
                      [showDelay]="150"
                      [hideDelay]="0"
                    >
                      i
                    </span>
                  }
                </div>
              }
              @case ("inputNumberHorizontal") {
                <div
                  class="flex flex-col w-full items-center justify-center"
                  (click)="
                    onInputCellClick($event, rowIndex, col.prop, 'inputNumber');
                    $event.stopPropagation()
                  "
                  (keydown.enter)="
                    onInputCellClick($event, rowIndex, col.prop, 'inputNumber')
                  "
                  (mousedown)="$event.stopPropagation()"
                >
                  <p-inputNumber
                    [attr.data-testid]="'table-input-number-horizontal-' + col.prop + '-' + rowIndex"
                    [(ngModel)]="rowData[col.prop]"
                    (ngModelChange)="
                      onInputNumberChange($event, rowData, col.prop)
                    "
                    (onBlur)="onInputNumberBlur($event, rowData, col.prop)"
                    (onMousedown)="$event.stopPropagation()"
                    [required]="col.required || false"
                    [min]="col.min || 0"
                    [max]="
                      col.max !== undefined
                        ? getMaxValue(col, rowData)
                        : 999999999
                    "
                    [step]="col.step || 1"
                    [placeholder]="
                      col.placeholder ? (col.placeholder | translate) : ''
                    "
                    [disabled]="
                      getColumnDisabled(col, rowData) || isRowDisabled(rowData)
                    "
                    [styleClass]="
                      getFieldError
                        ? getFieldError(rowIndex, col.prop)
                          ? 'w-full p-invalid'
                          : 'w-full'
                        : 'w-full'
                    "
                    [showButtons]="true"
                    buttonLayout="horizontal"
                    spinnerMode="horizontal"
                    [useGrouping]="true"
                    [minFractionDigits]="0"
                    [maxFractionDigits]="0"
                    [inputStyle]="{ textAlign: 'center', minWidth: '80px' }"
                    [inputId]="'input-' + rowIndex + '-' + col.prop"
                  >
                    <ng-template #incrementbuttonicon>
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6 2V10M2 6H10"
                          stroke="currentColor"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </ng-template>
                    <ng-template #decrementbuttonicon>
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M2 6H10"
                          stroke="currentColor"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </ng-template>
                  </p-inputNumber>
                  @if (getFieldError && getFieldError(rowIndex, col.prop)) {
                    <small class="text-red-500 text-xs mt-1 message-error">
                      {{ getFieldError(rowIndex, col.prop) }}
                    </small>
                  }
                </div>
              }
              @case ("inputText") {
                <div
                  class="flex flex-col w-full relative"
                  (click)="
                    onInputCellClick($event, rowIndex, col.prop, 'inputText');
                    $event.stopPropagation()
                  "
                  (keydown.enter)="
                    onInputCellClick($event, rowIndex, col.prop, 'inputText')
                  "
                  (mousedown)="$event.stopPropagation()"
                >
                  <input
                    [attr.data-testid]="'table-input-text-' + col.prop + '-' + rowIndex"
                    pInputText
                    [ngModel]="rowData[col.prop]"
                    (input)="onInputChange($event, rowData, col.prop)"
                    (blur)="onBlurChange(rowData, col.prop)"
                    (mousedown)="$event.stopPropagation()"
                    [required]="col.required || false"
                    [disabled]="
                      getColumnDisabled(col, rowData) || isRowDisabled(rowData)
                    "
                    [placeholder]="
                      col.placeholderProp
                        ? (rowData[col.placeholderProp] ?? '')
                        : col.placeholder
                          ? (col.placeholder | translate)
                          : ''
                    "
                    [class]="
                      getFieldError
                        ? getFieldError(rowIndex, col.prop)
                          ? 'w-full p-invalid'
                          : 'w-full'
                        : 'w-full'
                    "
                    class="w-full text-xs"
                    [style]="{ 'min-width': '120px', 'max-width': '100%' }"
                    [id]="'input-' + rowIndex + '-' + col.prop"
                  />
                  @if (getFieldError && getFieldError(rowIndex, col.prop)) {
                    <span
                      class="validation-icon absolute right-2 top-1/2 -translate-y-1/2 text-red-500 cursor-pointer"
                      [pTooltip]="getFieldError(rowIndex, col.prop) | translate"
                      tooltipPosition="top"
                      tooltipStyleClass="custom-tooltip"
                      [showDelay]="150"
                      [hideDelay]="0"
                    >
                      i
                    </span>
                  }
                </div>
              }
              @case ("inputDate") {
                <div class="flex flex-col w-full">
                  <p-datepicker
                    [attr.data-testid]="'table-datepicker-' + col.prop + '-' + rowIndex"
                    [(ngModel)]="rowData[col.prop]"
                    (onSelect)="onCellValueChange(rowData, col.prop, $event)"
                    [required]="col.required || false"
                    dateFormat="dd/mm/yy"
                    [placeholder]="
                      col.placeholder ? (col.placeholder | translate) : 'dd/mm/yyyy'
                    "
                    [showIcon]="true"
                    [showOnFocus]="false"
                    [inputId]="'datepicker-' + rowIndex + '-' + col.prop"
                    [appendTo]="'body'"
                    [class]="
                      getFieldError
                        ? getFieldError(rowIndex, col.prop)
                          ? 'w-full p-invalid'
                          : 'w-full'
                        : 'w-full'
                    "
                    [style]="{ 'min-width': '120px' }"
                  ></p-datepicker>
                  @if (getFieldError && getFieldError(rowIndex, col.prop)) {
                    <small class="text-red-500 text-xs mt-1 message-error">
                      {{ getFieldError(rowIndex, col.prop) }}
                    </small>
                  }
                </div>
              }
              @case ("date") {
                <span class="text-sm">
                  {{ formatDate(rowData[col.prop]) }}
                </span>
              }
              @case ("currency") {
                <span class="text-sm text-right">
                  {{ formatCurrency(rowData[col.prop]) }}
                </span>
              }
              @case ("number") {
                <span class="text-sm text-right">
                  {{ formatNumber(rowData[col.prop]) }}
                </span>
              }
              @default {
                {{ rowData[col.prop] }}
              }
            }
          </td>
        }
      </tr>
      @if (isRowExpanded(rowData)) {
        @for (child of getExpansionChildren(rowData); track child.skuCode) {
          <tr
            class="package-child-row"
            [attr.data-testid]="'table-package-child-' + rowIndex + '-' + $index"
          >
            @if (hasCheckColumn) {
              <td class="p-2 text-center text-gray-500 text-xs checked-cell">-</td>
            }
            @if (hasSerialColumn) {
              <td
                class="p-2 text-center text-gray-500 text-xs"
                [style.width.px]="getSerialColumnWidth()"
                [style.min-width.px]="getSerialColumnWidth()"
                [style.max-width.px]="getSerialColumnWidth()"
              >
                -
              </td>
            }
            @for (col of columns; track $index) {
              <td
                class="p-2 truncate-cell text-gray-500 text-xs"
                [ngClass]="{
                  'text-left': col.align === 'left',
                  'text-center': col.align === 'center' || !col.align,
                  'text-right': col.align === 'right',
                }"
                [ngStyle]="{
                  'max-width.px': getColumnWidth(col),
                  'min-width.px': getColumnWidth(col),
                }"
              >
                {{ getPackageChildColumnValue(child, col) }}
              </td>
            }
          </tr>
        }
      }
    </ng-template>

    <!-- No data message - always provide template; PrimeNG decides when to render -->
    <ng-template pTemplate="emptymessage">
      <tr data-testid="table-empty-row">
        <td
          data-testid="table-empty-cell"
          [attr.colspan]="
            hasCheckColumn
              ? hasSerialColumn
                ? columns.length + 2
                : columns.length + 1
              : hasSerialColumn
                ? columns.length + 1
                : columns.length
          "
          class="p-5 text-center text-gray-500"
        >
          <div class="flex flex-col items-center gap-3">
            <div class="text-sm text-gray-400">
              {{ noDataDescription | translate }}
            </div>
          </div>
        </td>
      </tr>
    </ng-template>
  </p-table>

  <!-- Loading Overlay -->
  @if (loading) {
    <div
      class="absolute inset-0 flex items-center justify-center z-10 bg-white/75"
    >
      <div class="flex flex-col items-center gap-2">
        <div
          data-testid="data-table-loading"
          class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"
        ></div>
        <div class="text-gray-600 text-sm">
          {{ "common.table.loading" | translate }}
        </div>
      </div>
    </div>
  }
</div>

<!-- Default templates -->
<ng-template #defaultStatusTemplate let-status>
  <span [class]="status ? 'text-green-600' : 'text-red-600'">
    {{
      status
        ? ("common.table.status.active" | translate)
        : ("common.table.status.inactive" | translate)
    }}
  </span>
</ng-template>

<ng-template #defaultActionsTemplate>
  <div class="flex gap-2 justify-center flex-wrap min-w-[160px]">
    <p-button
      data-testid="data-table-default-view-btn"
      type="button"
      [label]="'common.table.actions.view' | translate"
      [text]="true"
      size="small"
      class="whitespace-nowrap"
    ></p-button>
  </div>
</ng-template>

<!-- Bottom bar with paginator and Go to Page -->
@if (showPaginator && (dataSource.length > 0 || totalRecords > 0)) {
  <div class="flex justify-end items-center gap-4 mt-3 text-sm text-gray-400">
    <!-- Paginator -->
    <p-paginator
      data-testid="data-table-paginator"
      [rows]="rows"
      [first]="first"
      [totalRecords]="totalRecords"
      [rowsPerPageOptions]="rowsPerPageOptions"
      (onPageChange)="onPageChange($event)"
      [showCurrentPageReport]="true"
      [currentPageReportTemplate]="
        'common.table.pagination.current_page_report' | translate
      "
      class="p-paginator"
      [appendTo]="'body'"
    />
    <!-- Custom Go to Page -->
    <div class="flex items-center gap-2 mr-4">
      <label for="goto-page-input" class="text-sm text-gray-500">{{
        "common.table.pagination.go" | translate
      }}</label>
      <input
        data-testid="data-table-go-to-page"
        id="goto-page-input"
        type="number"
        [(ngModel)]="goToPageNumber"
        [min]="1"
        [max]="maxPage"
        (keyup.enter)="onGoToPage()"
        (blur)="onGoToPage()"
        class="w-16 h-9 text-center border rounded px-2"
      />
      <span class="text-sm text-gray-500">{{
        "common.table.pagination.page" | translate
      }}</span>
    </div>
  </div>
}
